import { Suspense, useEffect, useRef, useState, useMemo } from "react"
import ArrowBackButton from "../../../components/button/ArrowBackButton"
import { useAuth } from "../../../hooks/useAuth"
import useDetailCurrentProject from "./useDetailCurrentProject"
import Chart from "./components/Chart"
import ScoreDistribution from "./components/ScoreDistribution"
import CalibrationTable from "./components/CalibrationTableNew"
import { useParams } from "react-router-dom"
import Loading from "../../../components/loading/Loading"
import SendCalibrationManager from "./components/SendCalibrationManagerModal"
import SubmitModal from "./components/SubmitModal"
import SendCalibrationBack from "./components/SendCalibrationBackModal"
import { useModals } from "./hooks/useModals"
import { formatDeadlineDate, calculateRatingDeviation } from "./utils/calibrationUtils"
import ActionButtonGradient from "../../../components/button/ActionButtonGradient"

/**
 * DetailCurrentProject Component
 * Main component for viewing and managing calibration details for a project
 */
const DetailCurrentProject = () => {
    // Hooks
    const params = useParams()
    const { getCurrentUser } = useAuth()
    const { modals, openModal, closeModal } = useModals()
    
    // Custom hook for project data and operations
    const {
        automaticallyChangeRating,
        onChangeHandler,
        calibratedUsers,
        calibratedScore,
        countCalibrated,
        ratingQuota, 
        scoreDistribution,
        deviationRating,
        setDeviationRating,
        projectPhase,
        getProjectDetails,
        getProjectRemark,
        projectRemark,
        show,
        handleClose,
        handleShow,
        setCalibratedScore,
        onSubmitCalibrations,
        onSendCalibrationsToManager,
        onSendBackCalibrations,
        project,
        checkAllCalibratedNotExcessRatingQuota,
        averageAllScore,
        justificationIdNotFilled,
        canSubmit,
        phaseOption,
        alreadySendToManager, 
        canSendToManager,
        sendBackCalibrations,
        managerDeadline,
        getAllData,
        loadingPagestate,
        totalTopUser,
        updateTable,
        downloadReportFileExcel,
        businessPerformance,
        userNameOption,
        gradeOption,
        supervisorNameOption,
        divisionOption,
        filteredActualScore,
        filteredCountCalibrated,
        page,
        totalPage, 
        changePage,
        setPage,
        getUserCurrentProject,
        postChangeRatingScore,
        getTotalCalibratedScore,
        getAverageScore,
        ratingState,
        checkTopBottomRemark,
        sortUsersByJustificationStatus,
        sortUsersByJustificationStatusForSubmission
    } = useDetailCurrentProject()
    
    // Local state
    const [watch, setWatch] = useState(false)
    const [userID, setUserID] = useState()
    const [sendingToManager, setSendingToManager] = useState(false)
    const [sendingBack, setSendingBack] = useState(false)
    const [isSavingScore, setIsSavingScore] = useState(false)
    const [showScrollBottom, setShowScrollBottom] = useState(false)
    
    // Refs for controlling useEffect execution
    const isFirstRun = useRef(true)
    const hasInitialized = useRef(false)

    // Memoized values
    const isReadOnly = useMemo(() => watch || !canSubmit, [watch, canSubmit])
    const formattedDeadline = useMemo(() => 
        formatDeadlineDate(projectPhase?.EndDate), 
        [projectPhase]
    )
    const formattedManagerDeadline = useMemo(() => 
        formatDeadlineDate(managerDeadline), 
        [managerDeadline]
    )
    const excelDownloadLink = useMemo(() => 
        downloadReportFileExcel(userID, params.prev_calibrator, params.bu_performance, params.type, params.project_id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userID, params]
    )

    // Initialize project data
    useEffect(() => {
        if (params.project_id) {
            getProjectDetails(params.project_id)
            getProjectRemark(params.project_id)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.project_id])

    // Initialize user and load all data
    useEffect(() => {
        if (!hasInitialized.current) {
            const user = getCurrentUser()
            if (user?.ID) {
                hasInitialized.current = true
                setUserID(user.ID)
                getAllData(user, params)
                
                if (params.watch) {
                    setWatch(true)
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.bu_performance, params.prev_calibrator, params.watch, params.project_id, getCurrentUser, getAllData])
    
    // Calculate rating deviation
    useEffect(() => {
        // Only calculate if we have valid data (not just initial zeros)
        const hasValidQuota = ratingQuota.APlus > 0 || ratingQuota.A > 0 || ratingQuota.BPlus > 0 || 
        ratingQuota.B > 0 || ratingQuota.C > 0 || ratingQuota.D > 0
        const hasValidCount = countCalibrated.APlus > 0 || countCalibrated.A > 0 || countCalibrated.BPlus > 0 || 
        countCalibrated.B > 0 || countCalibrated.C > 0 || countCalibrated.D > 0
        
        if (hasValidQuota || hasValidCount) {
            const deviation = calculateRatingDeviation(countCalibrated, ratingQuota)
            setDeviationRating(deviation)
        }
    }, [countCalibrated, ratingQuota, setDeviationRating])

    // Scroll event listener for scroll-to-bottom button
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const scrollHeight = document.documentElement.scrollHeight
            const clientHeight = document.documentElement.clientHeight
            
            // Show button when scrolled down and not at bottom
            setShowScrollBottom(scrollTop > 300 && (scrollTop + clientHeight < scrollHeight - 100))
        }

        window.addEventListener('scroll', handleScroll)
        handleScroll() // Check initial position

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Check rating quota on first run after deviation is calculated
    useEffect(() => {
        if (isFirstRun.current && project && deviationRating) {
            isFirstRun.current = false
            checkAllCalibratedNotExcessRatingQuota()
        }
    }, [deviationRating, project, checkAllCalibratedNotExcessRatingQuota])

    // Auto-sort users without justification to the top when data loads
    useEffect(() => {
        if (calibratedUsers && calibratedUsers.length > 0 && calibratedScore && Object.keys(calibratedScore).length > 0 && userID) {
            // Small delay to ensure all data is properly loaded
            const timeoutId = setTimeout(() => {
                // Check if this is a submission page (has prevCalibrator OR type is "all") 
                if ((params.prev_calibrator && params.prev_calibrator !== "N-1") || params.type === "all") {
                    // Use enhanced function for submission pages that checks across all pages
                    sortUsersByJustificationStatusForSubmission(
                        userID, 
                        params.prev_calibrator || "", // Use empty string if no prev_calibrator
                        params.bu_performance, 
                        params.type, 
                        params.rating, 
                        params.project_id
                    );
                } else {
                    // Use normal sorting for individual BU pages
                    sortUsersByJustificationStatus();
                }
            }, 100)

            // Cleanup timeout on unmount or dependency change
            return () => clearTimeout(timeoutId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calibratedUsers?.length, Object.keys(calibratedScore || {}).length, userID, page]) // Only re-run when these specific values change

    // Modal handlers
    const handleSubmitCalibration = () => {
        closeModal('submit')
        onSubmitCalibrations(userID, params.project_id, params.bu_performance)
    }

    const handleSendToManager = async () => {
        closeModal('manager')
        setSendingToManager(true)
        try {
            await onSendCalibrationsToManager(userID, params.project_id, params.bu_performance, params.prev_calibrator)
        } finally {
            setSendingToManager(false)
        }
    }

    const scrollToBottom = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
        })
    }

    const handleSendBack = async () => {
        closeModal('back')
        setSendingBack(true)
        try {
            await onSendBackCalibrations(userID, params.project_id, params.bu_performance, params.prev_calibrator)
        } finally {
            setSendingBack(false)
        }
    }

    // Button visibility flags
    const showSendToManagerButton = !watch && canSendToManager && !alreadySendToManager
    const showSendBackButton = !watch && (!canSendToManager || alreadySendToManager) && sendBackCalibrations
    const showSubmitButton = !watch && canSubmit && !params.prev_calibrator
    const showChart = projectPhase?.ShowChart && params.type

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div className="container-fluid justify-content-center">
            <Suspense fallback={<Loading />}>
                <div>
                    {/* Header */}
                    <div style={{ marginBottom: '1.5rem', opacity: isSavingScore ? 0.5 : 1, pointerEvents: isSavingScore ? 'none' : 'auto' }}>
                        <ArrowBackButton title="My Task" path={-1} />
                    </div>

                    {/* Project Info */}
                    {!params.rating && (
                        <div className="flex-row" style={{ justifyContent: 'space-between' }}>
                            <p>
                                Previous Calibrator: {' '}
                                <b>{params.prev_calibrator_name || '-'}</b>
                            </p>
                            {formattedDeadline && (
                                <p>
                                    Deadline: <b>{formattedDeadline}</b>
                                </p>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex-row">
                        {showSendToManagerButton && (
                            <ActionButtonGradient
                                onClick={() => openModal('manager')}
                                variant="primary"
                                disabled={sendingToManager || isSavingScore}
                                children={sendingToManager ? (
                                    <>
                                        <div
                                            style={{
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #fff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite',
                                            }}
                                        />
                                        Sending...
                                    </>
                                ) : (
                                    'Send to Manager'
                                )}
                            />
                        )}
                        {showSendBackButton && (
                            <ActionButtonGradient
                                onClick={() => openModal('back')}
                                variant="primary"
                                disabled={sendingBack || isSavingScore}
                                children={sendingBack ? (
                                    <>
                                        <div
                                            style={{
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #fff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite',
                                            }}
                                        />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Calibrations Back'
                                )}
                            />
                        )}
                    </div>
                    {/* Chart and Score Distribution */}
                    {showChart && (
                        <>
                            <div className="table-responsive mt-4">
                                <table className="table align-items-center">
                                    <tbody>
                                        <tr>
                                            <td style={{ marginRight: '1em' }}>
                                                <Chart 
                                                    ratingQuota={ratingQuota}
                                                    actualScore={filteredActualScore}
                                                    calibratedScore={filteredCountCalibrated}
                                                />
                                            </td>
                                            <td>
                                                <ScoreDistribution 
                                                    project={project}
                                                    scoreDistribution={scoreDistribution}
                                                    ratingQuota={ratingQuota}
                                                    totalActualScore={filteredActualScore}
                                                    countCalibrated={countCalibrated}
                                                    deviationRating={deviationRating}
                                                    averageAllScore={averageAllScore}
                                                    businessPerformance={businessPerformance}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    {/* Calibration Table */}
                    <Suspense fallback={<Loading />}>
                        <CalibrationTable
                            calibratedUsers={calibratedUsers || []}
                            calibratedScore={calibratedScore}
                            setCalibratedScore={setCalibratedScore}
                            onChangeHandler={onChangeHandler}
                            automaticallyChangeRating={automaticallyChangeRating}
                            onSavingScoreChange={setIsSavingScore}
                            projectRemark={projectRemark}
                            projectPhase={projectPhase}
                            scoreDistribution={scoreDistribution}
                            watch={isReadOnly}
                            show={show}
                            handleClose={handleClose}
                            handleShow={handleShow}
                            project={project}
                            justificationIdNotFilled={justificationIdNotFilled}
                            phaseOption={phaseOption}
                            totalTopUser={totalTopUser}
                            updateTable={updateTable}
                            params={params}
                            excelLink={excelDownloadLink}
                            userNameOption={userNameOption}
                            gradeOption={gradeOption}
                            supervisorNameOption={supervisorNameOption}
                            divisionOption={divisionOption}
                            countCalibrated={countCalibrated}
                            calibratorID={userID}
                            page={page}
                            totalPage={totalPage}
                            changePage={changePage}
                            setPage={setPage}
                            getUserCurrentProject={getUserCurrentProject}
                            postChangeRatingScore={postChangeRatingScore}
                            getTotalCalibratedScore={getTotalCalibratedScore}
                            getAverageScore={getAverageScore}
                            ratingState={ratingState}
                            checkTopBottomRemark={checkTopBottomRemark}
                        />
                    </Suspense>

                    {/* Submit Button */}
                    {showSubmitButton && (
                        <div align="right" className="mt-3" style={{ opacity: isSavingScore ? 0.5 : 1, pointerEvents: isSavingScore ? 'none' : 'auto' }}>
                            <ActionButtonGradient
                                onClick={() => openModal('submit')}
                                variant="primary"
                                disabled={isSavingScore}
                                children={"Submit Calibrations"}
                            />
                        </div>
                    )}

                    {/* Modals */}
                    <div className="modal-container">
                        <SendCalibrationManager
                            show={modals.manager}
                            handleClose={() => closeModal('manager')}
                            handleOk={handleSendToManager}
                            title={params.prev_calibrator_name}
                            deadline={formattedManagerDeadline}
                        />
                        <SendCalibrationBack
                            show={modals.back}
                            handleClose={() => closeModal('back')}
                            handleOk={handleSendBack}
                            title={params.prev_calibrator_name}
                            deadline={formattedManagerDeadline}
                        />
                        <SubmitModal
                            show={modals.submit}
                            handleClose={() => closeModal('submit')}
                            handleOk={handleSubmitCalibration}
                        />
                    </div>
                </div>
            </Suspense>
            
            {/* Scroll to Bottom Button */}
            {showScrollBottom && (
                <button
                    onClick={scrollToBottom}
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '8px',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #02B4CC 0%, #0284C7 100%)',
                        border: 'none',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(2, 180, 204, 0.4)',
                        zIndex: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        transform: showScrollBottom ? 'translateY(0)' : 'translateY(100px)',
                        opacity: showScrollBottom ? 1 : 0
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(2, 180, 204, 0.5)'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(2, 180, 204, 0.4)'
                    }}
                    aria-label="Scroll to bottom"
                >
                    <svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                    >
                        <path d="M12 5v14M19 12l-7 7-7-7"/>
                    </svg>
                </button>
            )}
            
            {/* Loading Overlay */}
            <Loading state={loadingPagestate} />
        </div>
        </>
    )
}

export default DetailCurrentProject